import { isVariantAdditionOnly } from './draftEditsLiveStrings';
import type { RollbackDelta, VersionString } from './rollbackDelta';

/**
 * Whether a rollback changes text customers already see — the classification
 * that, inside a change-control window, makes an MCM required.
 *
 * The `draftEditsLiveStrings` sibling
 * for a rollback. A rollback is submitted as an ordinary submission, so the
 * backend applies the SAME gate to it (`ChangeControlGate.editsLiveStrings`;
 * design §8.3, https://chorus.aws.dev/doc/KxMqRG4cTs69) and this must answer
 * the gate's question identically — hence the shared
 * {@link isVariantAdditionOnly}, not a second copy of the comparison.
 *
 * The gate's exemptions, in its order:
 * - a `deleted` entry always counts as a live edit;
 * - `added` never counts (`NewStringsExempted`), so a delta that only
 *   reintroduces strings the current version lacks needs no MCM;
 * - an `edited` entry counts unless it is a pure variant addition
 *   (`VariantAdditionsExempted`).
 *
 * `delta.edited` is compared against the CURRENT version's snapshot, because
 * that is what is live: the delta already carries the target version's values,
 * and `computeRollbackDelta` files a string under `edited` whenever any
 * comparable field differs — including variants alone — so the list by itself
 * cannot answer this.
 *
 * One deliberate asymmetry with the draft path: `computeRollbackDelta`
 * normalizes a blank note to absent, so a string differing only by `''` vs
 * absent is not in the delta at all and never reaches this check, where the
 * draft path would treat it as a live edit. Both lean the same way the gate
 * does on the cases they do see, and the backend stays authoritative either way.
 *
 * An edited string with no counterpart in the current snapshot counts as a live
 * edit — it should not happen (the delta is computed from that same snapshot),
 * but erring towards asking for the MCM matches the draft path.
 */
export function rollbackEditsLiveStrings(
  delta: RollbackDelta | null | undefined,
  currentStrings: readonly VersionString[],
): boolean {
  if (!delta) {
    return false;
  }
  if (delta.deleted.length > 0) {
    return true;
  }
  const liveById = new Map(currentStrings.map((s) => [s.stringId, s]));
  return delta.edited.some((edited) => {
    const live = liveById.get(edited.stringId);
    return live === undefined || !isVariantAdditionOnly(edited, live);
  });
}
